import { getQueryParams, QueryParams } from "types/queryParams.types";
import { baseApi } from "./baseApi";

const URL_NOTIFICATIONS = "admin/notifications";

export interface NotificationModel {
  id: string;
  receiverUserId: string;
  senderUserId: string | null;
  title: string;
  body: string;
  type: string;
  orderId: string | null;
  containerId: string | null;
  routeId: string | null;
  transportCostId: string | null;
  status: "read" | "unread";
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListMeta {
  count?: number;
  total?: number;
}

export interface NotificationListData {
  count?: number;
  total?: number;
  rows?: NotificationModel[];
  items?: NotificationModel[];
  meta?: NotificationListMeta;
}

export interface NotificationListResponse {
  statusCode?: number;
  success?: boolean;
  count?: number;
  total?: number;
  meta?: NotificationListMeta;
  data: NotificationListData | NotificationModel[];
}

interface NotificationDetailResponse {
  statusCode?: number;
  success?: boolean;
  data: NotificationModel;
}

interface MarkAllAsReadResponse {
  statusCode?: number;
  success?: boolean;
  data: {
    updatedCount: number;
    success: boolean;
  };
}

interface GetNotificationsArgs {
  query?: QueryParams;
}

interface MarkNotificationAsReadArgs {
  id: string;
}

export const notificationsApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<
      NotificationListResponse,
      GetNotificationsArgs | void
    >({
      query: (params) => {
        const queryParams = params?.query;
        const query = queryParams ? getQueryParams(queryParams) : "";

        if (query === "") {
          return {
            url: URL_NOTIFICATIONS,
            method: "GET",
          };
        }

        return {
          url: `${URL_NOTIFICATIONS}?${query}`,
          method: "GET",
        };
      },
      providesTags: ["NOTIFICATION"],
    }),

    markNotificationAsRead: builder.mutation<
      NotificationDetailResponse,
      MarkNotificationAsReadArgs
    >({
      query: ({ id }) => ({
        url: `${URL_NOTIFICATIONS}/${id}`,
        method: "PATCH",
        data: {
          status: "read",
        },
      }),
      invalidatesTags: ["NOTIFICATION"],
    }),

    markAllNotificationsAsRead: builder.mutation<MarkAllAsReadResponse, void>({
      query: () => ({
        url: `${URL_NOTIFICATIONS}/read-all`,
        method: "PATCH",
      }),
      invalidatesTags: ["NOTIFICATION"],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
} = notificationsApiService;
